import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {useNavigate} from 'react-router';
import ConfirmDialog from '@/components/confirm-dialog';
import {
  NewDocumentCreationDialogAction,
  useCreateDocumentMutation,
} from '@/components/new-document-creation/hooks/use-create-document-mutation';
import {useDocuments} from '@/hooks/queries/documents';
import {Toaster} from '@/initializers/toaster';
import {isForbiddenError} from '@/utils/error-handling';
import {useConfirmDialogActions} from '../confirm-dialog/use-confirm-dialog-actions';
import {useNewDocumentCreation} from './context/new-document-creation.hook';
import NewDocumentCreationDialog from './dialog';

const FORM_PATH = '/app/form/';

const NewDocumentCreation = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const {isDialogVisible, isConfirmationDialogVisible, hideDialog, showConfirmationDialog, hideConfirmationDialog} =
    useNewDocumentCreation();

  const [newDocumentCreationAction, setNewDocumentCreationAction] = useState<NewDocumentCreationDialogAction>();

  const {data: documents, refetch: refetchDocuments} = useDocuments({
    enabled: isDialogVisible,
  });

  const {mutate: createDocument, isPending: isCreatingDocument} = useCreateDocumentMutation({
    onError: error => {
      const feedbackMessage = t(
        `components.document-menu.document-list.item.feedback-message.create.error.${isForbiddenError(error) ? 'forbidden' : 'default'}`,
      );
      Toaster.error(feedbackMessage);
    },
    onSuccess: ({docId, state}) => {
      refetchDocuments();
      hideConfirmationDialog();
      hideDialog();
      navigate(`${FORM_PATH}${docId}`, {state});
    },
  });

  const onConfirmDocumentCreation = useCallback(
    (action: NewDocumentCreationDialogAction) => {
      if (location.pathname.startsWith(FORM_PATH)) {
        setNewDocumentCreationAction(action);
        hideDialog();
        showConfirmationDialog();
        return;
      }

      createDocument(action);
    },
    [createDocument, showConfirmationDialog, hideDialog],
  );

  const onConfirmDocumentCreationConfirmation = useCallback(() => {
    if (!newDocumentCreationAction) return;
    createDocument(newDocumentCreationAction);
  }, [newDocumentCreationAction, createDocument]);

  const confirmDialogActions = useConfirmDialogActions({
    onCancel: hideConfirmationDialog,
    onConfirm: onConfirmDocumentCreationConfirmation,
  });

  return (
    <>
      <NewDocumentCreationDialog
        latestTransaction={documents?.latestTransaction}
        isVisible={isDialogVisible}
        isLoading={isCreatingDocument}
        onClose={hideDialog}
        onConfirm={onConfirmDocumentCreation}
      />

      <ConfirmDialog
        isVisible={isConfirmationDialogVisible}
        isLoading={isCreatingDocument}
        loadingMode="dialog"
        text={t('components.new-document-creation.confirm-dialog.text')}
        title={t('components.new-document-creation.confirm-dialog.title')}
        actions={confirmDialogActions}
      />
    </>
  );
};

export default NewDocumentCreation;
